import { ProductPagination, ProductStatusBadge } from "@/components";
import { Alert, Button, DatePicker, Empty, Input, Select, Skeleton, Table, Tooltip } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import {
  CalendarDaysIcon,
  EyeIcon,
  MapPinIcon,
  PlusIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  SearchIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  PortalBookingStatus,
  PortalItineraryListFilters,
  PortalItineraryListItem,
  PortalOptionStatus,
  usePortalItinerariesQuery,
} from "../actions/itinerary.actions";
import { PortalPageHeader, PortalPageShell } from "../components/PortalProduct";
import { getPortalPaths } from "../lib/routes";

const { RangePicker } = DatePicker;
const requestStatuses: PortalOptionStatus[] = ["draft", "submitted", "confirmed", "cancelled"];
const bookingStatuses: PortalBookingStatus[] = ["pending", "in_progress", "completed", "cancelled"];

/** Quote statuses that can appear in Bookings: a confirmed quote or a started booking. */
const bookingsRequestStatuses: PortalOptionStatus[] = ["submitted", "confirmed"];

const parseRequestStatus = (value: string | null, allowed: PortalOptionStatus[]): PortalOptionStatus | undefined =>
  allowed.includes(value as PortalOptionStatus) ? (value as PortalOptionStatus) : undefined;

const parseBookingStatus = (value: string | null): PortalBookingStatus | undefined =>
  bookingStatuses.includes(value as PortalBookingStatus) ? (value as PortalBookingStatus) : undefined;

const statusTone = (key: string): "gold" | "neutral" | "success" | "danger" => {
  if (key === "confirmed" || key === "completed") return "success";
  if (key === "cancelled") return "danger";
  if (key === "submitted" || key === "in_progress") return "gold";
  return "neutral";
};

const formatDate = (value: string | null) => (value ? dayjs(value).format("DD MMM YYYY") : "Not set");
const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);

/**
 * Renders the agency-wide portal itinerary list with server-side controls.
 * In `bookings` mode it lists only confirmed quotes and started bookings.
 */
const PortalItineraryListPage = ({ mode = "itineraries" }: { mode?: "itineraries" | "bookings" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBookings = mode === "bookings";
  const requestedStatus = parseRequestStatus(searchParams.get("status"), isBookings ? bookingsRequestStatuses : requestStatuses);
  const requestedBookingStatus = parseBookingStatus(searchParams.get("bookingStatus"));
  const [filters, setFilters] = useState<PortalItineraryListFilters>(() => ({
    page: 1,
    limit: 20,
    sortBy: "updatedAt",
    sortOrder: "DESC",
    status: requestedStatus,
    bookingStatus: requestedBookingStatus,
    search: searchParams.get("search")?.trim().slice(0, 120) || undefined,
  }));
  const [search, setSearch] = useState(() => searchParams.get("search")?.trim().slice(0, 120) || "");
  // The scope comes from the page mode on every render, so it can never carry over between routes.
  const queryFilters = useMemo<PortalItineraryListFilters>(
    () => ({ ...filters, scope: isBookings ? "bookings" : undefined }),
    [filters, isBookings],
  );
  const query = usePortalItinerariesQuery(queryFilters);
  const portalPaths = getPortalPaths();

  const updateFilter = <Key extends keyof PortalItineraryListFilters>(
    key: Key,
    value: PortalItineraryListFilters[Key],
  ) => {
    setFilters((current) => ({ ...current, [key]: value || undefined, page: key === "page" ? Number(value) : 1 }));
    if (key === "status" || key === "bookingStatus") {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (value) nextSearchParams.set(key, String(value));
      else nextSearchParams.delete(key);
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  useEffect(() => {
    setFilters((current) => {
      if (current.status === requestedStatus && current.bookingStatus === requestedBookingStatus) return current;
      return { ...current, status: requestedStatus, bookingStatus: requestedBookingStatus, page: 1 };
    });
  }, [requestedBookingStatus, requestedStatus]);

  const clearFilters = () => {
    setSearch("");
    setFilters({ page: 1, limit: 20, sortBy: "updatedAt", sortOrder: "DESC" });
    setSearchParams({}, { replace: true });
  };

  const openItinerary = useCallback((id: string) => navigate(portalPaths.itinerary(id)), [navigate, portalPaths]);

  const columns: ColumnsType<PortalItineraryListItem> = useMemo(
    () => [
      {
        title: "Request",
        dataIndex: "reference",
        key: "reference",
        width: 120,
        sorter: true,
        render: (reference: string, item) => (
          <div>
            <Link
              to={portalPaths.itinerary(item.id)}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Open quote ${reference}`}
              className="block font-semibold text-[#131B2A] no-underline hover:text-(--brand-primary)"
            >
              {reference}
            </Link>
            {item.optionNumber > 1 ? <span className="text-xs text-gray-500">Option {item.optionNumber}</span> : null}
          </div>
        ),
      },
      {
        title: "Destination",
        dataIndex: "destinations",
        key: "destination",
        width: 230,
        sorter: true,
        render: (destinations: string[]) => (
          <span className="flex items-center gap-2 font-medium text-[#131B2A]">
            <MapPinIcon size={15} className="shrink-0 text-gray-400" />
            {destinations.join(", ") || "Not set"}
          </span>
        ),
      },
      {
        title: "Travel dates",
        key: "startDate",
        width: 205,
        sorter: true,
        render: (_, item) => (
          <div>
            <div>
              {formatDate(item.startDate)} – {formatDate(item.endDate)}
            </div>
            <div className="text-xs text-gray-500">{item.nights} nights</div>
          </div>
        ),
      },
      {
        title: "Travellers",
        key: "travellers",
        width: 130,
        render: (_, item) => (
          <div>
            <div className="flex items-center gap-1.5">
              <UsersRoundIcon size={14} /> {item.travellers.total}
            </div>
            <div className="text-xs text-gray-500">{item.travellers.summary}</div>
          </div>
        ),
      },
      {
        title: "Total price",
        key: "totalPrice",
        width: 145,
        align: "right",
        render: (_, item) => (
          <span className="font-semibold tabular-nums text-[#131B2A]">
            {formatMoney(item.totalPrice.amount, item.totalPrice.currency)}
          </span>
        ),
      },
      {
        title: "Option status",
        key: "requestStatus",
        width: 145,
        sorter: true,
        render: (_, item) => (
          <div className="flex flex-col items-start gap-1">
            <ProductStatusBadge tone={statusTone(item.requestStatus.key)}>
              {item.requestStatus.label}
            </ProductStatusBadge>
            {item.isSubmittedOption ? (
              <span className="text-[11px] font-semibold text-(--brand-primary)">Confirmed quote</span>
            ) : null}
          </div>
        ),
      },
      {
        title: "Booking progress",
        key: "bookingStatus",
        width: 155,
        sorter: true,
        render: (_, item) => (
          <ProductStatusBadge tone={statusTone(item.bookingStatus.key)}>{item.bookingStatus.label}</ProductStatusBadge>
        ),
      },
      {
        title: "Guest name",
        dataIndex: "guestName",
        key: "guestName",
        width: 190,
        sorter: true,
        render: (guestName: string | null) => (
          <span className="block truncate text-[#131B2A]" title={guestName || undefined}>
            {guestName || "Unnamed guest"}
          </span>
        ),
      },
      {
        title: "Confirmed",
        key: "submittedAt",
        width: 140,
        sorter: true,
        render: (_, item) => (item.submittedAt ? formatDate(item.submittedAt) : "Not confirmed"),
      },
      {
        title: "Updated",
        key: "updatedAt",
        width: 140,
        sorter: true,
        render: (_, item) => formatDate(item.updatedAt),
      },
      {
        title: "",
        key: "action",
        width: 74,
        fixed: "right",
        render: (_, item) => (
          <Button
            type="text"
            aria-label={`View ${item.reference}`}
            icon={<EyeIcon size={16} />}
            onClick={() => openItinerary(item.id)}
            className="hover:!bg-(--brand-primary)/10 hover:!text-(--brand-primary)"
          />
        ),
      },
    ],
    [openItinerary, portalPaths],
  );

  const handleTableChange: TableProps<PortalItineraryListItem>["onChange"] = (_, __, sorter) => {
    if (Array.isArray(sorter) || !sorter.columnKey || !sorter.order) return;
    setFilters((current) => ({
      ...current,
      page: 1,
      sortBy: sorter.columnKey as PortalItineraryListFilters["sortBy"],
      sortOrder: sorter.order === "ascend" ? "ASC" : "DESC",
    }));
  };

  return (
    <PortalPageShell>
      <div className="space-y-3">
        <PortalPageHeader
          title={isBookings ? "Bookings" : "Itineraries"}
          meta={`${(query.data?.pagination.total || 0).toLocaleString()} in this workspace`}
          description={isBookings ? "Confirmed quotes and bookings that have started." : "Search quote options across all trips."}
          actions={
            <>
              <Tooltip title="Refresh itineraries">
                <Button
                  aria-label="Refresh itineraries"
                  icon={<RefreshCwIcon size={15} />}
                  loading={query.isFetching}
                  onClick={() => void query.refetch()}
                />
              </Tooltip>
              {isBookings ? null : (
                <Link to={portalPaths.newRequest}>
                  <Button type="primary" icon={<PlusIcon size={15} />}>
                    Create with AI
                  </Button>
                </Link>
              )}
            </>
          }
        />

        <section
          aria-label="Itinerary filters"
          className="rounded-lg border border-gray-200 bg-white p-2 shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
        >
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-6">
            <Input
              allowClear
              value={search}
              prefix={<SearchIcon size={15} className="text-gray-400" />}
              placeholder="Reference or destination"
              onChange={(event) => setSearch(event.target.value)}
              onPressEnter={() => updateFilter("search", search.trim())}
            />
            <Select
              allowClear
              placeholder="Option status"
              value={filters.status}
              onChange={(value) => updateFilter("status", value)}
              options={[
                ...(isBookings ? [] : [{ value: "draft", label: "Saved" }]),
                { value: "submitted", label: "Confirmed" },
                { value: "confirmed", label: "Booking started" },
                ...(isBookings ? [] : [{ value: "cancelled", label: "Cancelled" }]),
              ]}
            />
            <Select
              allowClear
              placeholder="Booking progress"
              value={filters.bookingStatus}
              onChange={(value) => updateFilter("bookingStatus", value)}
              options={[
                { value: "pending", label: "Pending" },
                { value: "in_progress", label: "In progress" },
                { value: "completed", label: "Completed" },
                { value: "cancelled", label: "Cancelled" },
              ]}
            />
            <Input
              allowClear
              prefix={<MapPinIcon size={15} className="text-gray-400" />}
              placeholder="Destination"
              value={filters.destination}
              onChange={(event) => updateFilter("destination", event.target.value)}
            />
            <RangePicker
              className="w-full"
              placeholder={["Travel from", "Travel to"]}
              onChange={(dates) =>
                setFilters((current) => ({
                  ...current,
                  page: 1,
                  startDateFrom: dates?.[0]?.format("YYYY-MM-DD"),
                  startDateTo: dates?.[1]?.format("YYYY-MM-DD"),
                }))
              }
            />
            <RangePicker
              className="w-full"
              placeholder={["Confirmed from", "Confirmed to"]}
              onChange={(dates) =>
                setFilters((current) => ({
                  ...current,
                  page: 1,
                  submittedFrom: dates?.[0]?.format("YYYY-MM-DD"),
                  submittedTo: dates?.[1]?.format("YYYY-MM-DD"),
                }))
              }
            />
          </div>
          <div className="mt-2 flex flex-wrap justify-between gap-2 border-t border-gray-100 pt-2">
            <Button icon={<SearchIcon size={14} />} onClick={() => updateFilter("search", search.trim())}>
              Apply search
            </Button>
            <Button type="text" icon={<RotateCcwIcon size={14} />} onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        </section>

        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load itineraries"
            description="Please try again."
            action={
              <Button size="small" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
          <div className="hidden md:block">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={query.data?.items || []}
              loading={query.isLoading || query.isFetching}
              pagination={false}
              size="middle"
              className="[&_.ant-table]:!rounded-none [&_.ant-table-cell]:!border-gray-100 [&_.ant-table-thead_.ant-table-cell]:!py-3 [&_.ant-table-thead_.ant-table-cell]:!text-[11px] [&_.ant-table-thead_.ant-table-cell]:!font-semibold [&_.ant-table-thead_.ant-table-cell]:!uppercase [&_.ant-table-thead_.ant-table-cell]:!tracking-[0.04em] [&_.ant-table-tbody_.ant-table-cell]:!py-3.5"
              scroll={{ x: 1560 }}
              onChange={handleTableChange}
              onRow={(item) => ({
                onClick: (event) => {
                  if ((event.target as HTMLElement).closest("button, a")) return;
                  openItinerary(item.id);
                },
              })}
              rowClassName="cursor-pointer transition-colors duration-150 hover:!bg-[var(--brand-primary-soft)] active:!bg-[var(--brand-primary-soft)]"
              locale={{
                emptyText: (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No itineraries match these filters" />
                ),
              }}
            />
          </div>
          <div className="space-y-3 p-3 md:hidden">
            {query.isLoading ? Array.from({ length: 3 }, (_, index) => <Skeleton key={index} active />) : null}
            {!query.isLoading && !query.data?.items.length ? (
              <Empty description="No itineraries match these filters" />
            ) : null}
            {query.data?.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openItinerary(item.id)}
                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-(--brand-primary) hover:bg-[var(--brand-primary-soft)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#131B2A]">{item.guestName || "Unnamed guest"}</div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {item.reference} · Option {item.optionNumber}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                      <MapPinIcon size={14} /> {item.destinations.join(", ")}
                    </div>
                  </div>
                  <EyeIcon size={16} className="shrink-0 text-gray-400" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <CalendarDaysIcon size={13} /> {formatDate(item.startDate)}
                  </span>
                  <span className="flex items-center gap-1">
                    <UsersRoundIcon size={13} /> {item.travellers.total} travellers
                  </span>
                </div>
                <div className="mt-3 text-sm font-semibold text-[#131B2A]">
                  {formatMoney(item.totalPrice.amount, item.totalPrice.currency)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ProductStatusBadge tone={statusTone(item.requestStatus.key)}>
                    {item.requestStatus.label}
                  </ProductStatusBadge>
                  <ProductStatusBadge tone={statusTone(item.bookingStatus.key)}>
                    {item.bookingStatus.label}
                  </ProductStatusBadge>
                </div>
              </button>
            ))}
          </div>
          {query.data?.pagination.total ? (
            <ProductPagination
              current={query.data.pagination.page}
              pageSize={query.data.pagination.limit}
              total={query.data.pagination.total}
              itemLabel="itineraries"
              onChange={(page, pageSize) => setFilters((current) => ({ ...current, page, limit: pageSize }))}
              onPageSizeChange={(limit) => setFilters((current) => ({ ...current, page: 1, limit }))}
            />
          ) : null}
        </section>
      </div>
    </PortalPageShell>
  );
};

export default PortalItineraryListPage;
